/**
 * Popup Promotion Component
 * Handles popup display, delay timer, and scroll to newsletter functionality
 */

class PopupPromotion extends HTMLElement {
  constructor() {
    super();
    this.delay = parseInt(this.dataset.delay) || 5;
    // Cap reappear days at 7 (1 week max)
    this.reappearDays = Math.min(parseInt(this.dataset.reappearDays) || 7, 7);
    this.hasShown = false;
    this.overlay = this.querySelector(".popup-promotion__overlay");
    this.closeButton = this.querySelector(".popup-promotion__close");
    this.content = this.querySelector(".popup-promotion__content");

    // Check if popup should be shown based on localStorage
    const popupId = this.id;
    const closedKey = `popup_closed_${popupId}`;
    const closedTimestamp = localStorage.getItem(closedKey);

    if (closedTimestamp) {
      const daysSinceClosed = (Date.now() - parseInt(closedTimestamp)) / (1000 * 60 * 60 * 24);

      // If not enough days have passed, don't show the popup
      if (daysSinceClosed < this.reappearDays) {
        return; // Don't show popup yet
      } else {
        // Enough days have passed, remove the old timestamp
        localStorage.removeItem(closedKey);
      }
    }

    this.init();
  }

  init() {
    // Set up event listeners
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.hide());
    }

    if (this.overlay) {
      this.overlay.addEventListener("click", () => this.hide());
    }

    // Handle escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.hasAttribute("open")) {
        this.hide();
      }
    });

    // Process links in text content
    this.processLinks();

    // Show popup after delay
    setTimeout(() => {
      this.show();
    }, this.delay * 1000);
  }

  processLinks() {
    // Find all links with class 'popup-link-subscribe' and add click handler
    const subscribeLinks = this.querySelectorAll('.popup-link-subscribe, a[href="#newsletter"]');

    subscribeLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        this.hide();
        this.scrollToNewsletter();
      });
    });

    // Also check for the words "subscribed" and "subscribe" in text and make them clickable if not already a link
    const textElements = this.querySelectorAll(".popup-promotion__text, .popup-promotion__conditions");
    textElements.forEach((element) => {
      const text = element.innerHTML;

      // We'll match both "subscribed" and "subscribe", but not if they're already in a link
      // This regex matches <a ...>subscribe(d)</a> (case-insensitive)
      const alreadyLinked = /<a[^>]*>.*?(subscribed|subscribe).*?<\/a>/i;

      if (text && (/\bsubscribed\b/i.test(text) || /\bsubscribe\b/i.test(text)) && !alreadyLinked.test(text)) {
        // Replace both 'subscribed' and 'subscribe' with the link
        // The callback ensures we preserve original capitalization and form
        const regex = /(\b)(subscribe(?:d)?)(\b)/gi;
        element.innerHTML = text.replace(regex, (match, p1, p2, p3) => {
          return (
            p1 +
            `<a href="#newsletter" class="popup-link-subscribe" style="text-decoration: underline; cursor: pointer; font-weight: inherit;">${p2}</a>` +
            p3
          );
        });

        // Re-attach event listener to any new link(s)
        const newLinks = element.querySelectorAll(".popup-link-subscribe");
        newLinks.forEach((newLink) => {
          newLink.addEventListener("click", (e) => {
            e.preventDefault();
            this.hide();
            this.scrollToNewsletter();
          });
        });
      }
    });
  }

  show() {
    if (this.hasShown) return;

    this.setAttribute("open", "");
    document.body.classList.add("popup-promotion-open");
    this.hasShown = true;

    // Focus trap
    this.trapFocus();
  }

  hide() {
    this.removeAttribute("open");
    document.body.classList.remove("popup-promotion-open");

    // Store timestamp when popup is closed in localStorage
    const popupId = this.id;
    const closedKey = `popup_closed_${popupId}`;
    localStorage.setItem(closedKey, Date.now().toString());

    // Remove focus trap
    this.removeFocusTrap();
  }

  scrollToNewsletter() {
    // Wait a bit for popup to close
    setTimeout(() => {
      // Try multiple selectors to find newsletter form
      const newsletterSelectors = [
        "#ContactFooter",
        ".footer__newsletter",
        ".newsletter-form",
        ".newsletter--wrapper",
        'footer form[action*="contact"]',
      ];

      let newsletterElement = null;
      for (const selector of newsletterSelectors) {
        newsletterElement = document.querySelector(selector);
        if (newsletterElement) break;
      }

      if (newsletterElement) {
        newsletterElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Focus on email input if available
        setTimeout(() => {
          const emailInput = newsletterElement.querySelector('input[type="email"]');
          if (emailInput) {
            emailInput.focus();
          }
        }, 500);
      } else {
        // Fallback: scroll to footer
        const footer = document.querySelector("footer");
        if (footer) {
          footer.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }, 300);
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input[type="email"], input[type="text"], input[type="submit"], select'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.handleTabKey = (e) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", this.handleTabKey);
    firstElement.focus();
  }

  removeFocusTrap() {
    if (this.handleTabKey) {
      document.removeEventListener("keydown", this.handleTabKey);
    }
  }
}

customElements.define("popup-promotion", PopupPromotion);
